import React from 'react';
import {
  Box, Grid, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Typography, IconButton, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { postRequest } from 'Includes/api/apicall';
import { POST_URL } from 'Includes/urls';
import Swal from 'sweetalert2';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer_unaccountable', label: 'Bank Transfer (Unaccountable)' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'gateway', label: 'Payment Gateway' },
];

function CreateAnotherExpenseModal({ 
  open, 
  onClose, 
  selectedExpenses, 
  expenseTypes, 
  onSuccess,
  props
}) {
  const [expenses, setExpenses] = React.useState([]);
  const [expenseDetails, setExpenseDetails] = React.useState({});
  const [expenseErrors, setExpenseErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [commonExpenseDate, setCommonExpenseDate] = React.useState(getTodayDate());
  const [commonMop, setCommonMop] = React.useState('');
  const [commonNotes, setCommonNotes] = React.useState('');

  // Initialize expenses from selected expenses (excluding expense_date and expense_receipt)
  React.useEffect(() => {
    if (open && selectedExpenses && selectedExpenses.length > 0) {
      // Get payment method and notes from the first selected expense (assuming all have same values)
      const firstExpense = selectedExpenses[0];
      const mop = firstExpense.mop || '';
      const notes = firstExpense.notes || '';
      setCommonMop(mop);
      setCommonNotes(notes);
      
      const expensesData = selectedExpenses.map((expense, index) => ({
        id: index,
        expense_type: expense.expense_type || '',
        name: expense.name || '',
        expense_date: '', // Not pre-filled
        amount: expense.amount || '',
        reciever: expense.reciever || '',
        mop: mop, // Pre-filled from previous data
        reference: '', // Not pre-filled
        notes: notes, // Pre-filled from previous data
        expense_receipt_file: null, // Not pre-filled
      }));

      setExpenses(expensesData.map((_, idx) => ({ id: idx })));
      const detailsObj = {};
      expensesData.forEach((data, idx) => {
        detailsObj[idx] = data;
      });
      setExpenseDetails(detailsObj);
      setExpenseErrors({});
    }
  }, [open, selectedExpenses]);

  const handleClose = () => {
    setExpenses([]);
    setExpenseDetails({});
    setExpenseErrors({});
    setCommonExpenseDate(getTodayDate()); // Reset to today's date
    setCommonMop('');
    setCommonNotes('');
    onClose();
  };

  const handleApplyDateToAll = () => {
    if (commonExpenseDate) {
      const updatedDetails = { ...expenseDetails };
      expenses.forEach((expense) => {
        if (updatedDetails[expense.id]) {
          updatedDetails[expense.id] = {
            ...updatedDetails[expense.id],
            expense_date: commonExpenseDate
          };
        }
      });
      setExpenseDetails(updatedDetails);
      
      // Clear errors for expense_date fields
      const newErrors = { ...expenseErrors };
      expenses.forEach((expense) => {
        delete newErrors[`${expense.id}_expense_date`];
      });
      setExpenseErrors(newErrors);
    }
  };

  const addExpense = () => {
    const newId = expenses.length > 0 ? Math.max(...expenses.map(e => e.id)) + 1 : 0;
    setExpenses([...expenses, { id: newId }]);
    setExpenseDetails(prev => ({
      ...prev,
      [newId]: {
        expense_type: '',
        name: '',
        expense_date: commonExpenseDate || '', // Use common date if available
        amount: '',
        reciever: '',
        mop: commonMop, // Use common payment method from previous data
        reference: '',
        notes: commonNotes, // Use common notes from previous data
        expense_receipt_file: null,
      }
    }));
  };

  const removeExpense = (expenseId) => {
    if (expenses.length > 1) {
      setExpenses(expenses.filter(e => e.id !== expenseId));
      const newDetails = { ...expenseDetails };
      delete newDetails[expenseId];
      setExpenseDetails(newDetails);
      
      const newErrors = { ...expenseErrors };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`${expenseId}_`)) {
          delete newErrors[key];
        }
      });
      setExpenseErrors(newErrors);
    }
  };

  const handleFieldChange = (expenseId, fieldName, value) => {
    setExpenseDetails(prev => ({
      ...prev,
      [expenseId]: {
        ...prev[expenseId],
        [fieldName]: value
      }
    }));
    
    // Clear error for this field
    setExpenseErrors(prev => ({
      ...prev,
      [`${expenseId}_${fieldName}`]: ''
    }));
  };

  const handleFileChange = (expenseId, file) => {
    setExpenseDetails(prev => ({
      ...prev,
      [expenseId]: {
        ...prev[expenseId],
        expense_receipt_file: file
      }
    }));
  };

  const validate = () => {
    const errors = {};
    let isValid = true;

    expenses.forEach((expense) => {
      const data = expenseDetails[expense.id];
      
      if (!data.name || data.name.trim() === '') {
        errors[`${expense.id}_name`] = 'Name is required';
        isValid = false;
      }
      
      if (!data.expense_date) {
        errors[`${expense.id}_expense_date`] = 'Expense Date is required';
        isValid = false;
      }
      
      if (!data.amount || data.amount.toString().trim() === '' || parseFloat(data.amount) <= 0) {
        errors[`${expense.id}_amount`] = 'Amount is required and must be greater than 0';
        isValid = false;
      }
      
      if (!data.reciever || data.reciever.trim() === '') {
        errors[`${expense.id}_reciever`] = 'Receiver is required';
        isValid = false;
      }
    });

    setExpenseErrors(errors);
    return isValid;
  };

  const handleSubmit = () => {
    if (validate()) {
      setLoading(true);
      
      // Prepare FormData with expense data and files
      const formData = new FormData();
      
      // Prepare expenses data array
      const expensesData = expenses.map((expense) => {
        const data = expenseDetails[expense.id];
        return {
          expense_type: data.expense_type, // Required, don't allow null
          name: data.name.trim(),
          expense_date: data.expense_date,
          amount: parseFloat(data.amount),
          reciever: data.reciever.trim(),
          mop: data.mop, // Required
          reference: data.reference.trim() || '',
          notes: data.notes.trim() || '',
        };
      });
      
      // Append expenses data as JSON string
      formData.append('expenses', JSON.stringify(expensesData));
      
      // Append files with indexed keys
      expenses.forEach((expense, index) => {
        const data = expenseDetails[expense.id];
        if (data.expense_receipt_file) {
          formData.append(`expense_receipt_${index}`, data.expense_receipt_file);
        }
      });

      const url = POST_URL.expenses.api;
      postRequest(url, formData, props || {}).then(response => {
        setLoading(false);
        if (response && response.status === 200) {
          Swal.fire({
            position: 'top-end',
            type: 'success',
            title: response.data.Reason || 'Success',
            text: 'Expense(s) created successfully',
            showConfirmButton: false,
            timer: 1500
          });
          handleClose();
          if (onSuccess) {
            onSuccess();
          }
        }
      }).catch((error) => {
        setLoading(false);
        const errorMessage = error?.response?.data?.Reason || error?.response?.data?.name?.[0] || 'Failed to create expense(s)';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          timer: 2000,
          showConfirmButton: false
        });
      });
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Create Another Expense ({expenses.length})
          </Typography>
          <IconButton 
            color="primary" 
            onClick={addExpense} 
            size="small" 
            title="Add another expense"
          >
            <AddIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          {/* Common Expense Date Section */}
          <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="h6" gutterBottom>
              Common Settings
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Expense Date"
                  type="date"
                  fullWidth
                  size="small"
                  value={commonExpenseDate}
                  onChange={(e) => setCommonExpenseDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleApplyDateToAll}
                  disabled={!commonExpenseDate}
                  fullWidth
                >
                  Apply for All
                </Button>
              </Grid>
            </Grid>
          </Box>

          {expenses.map((expense, index) => (
            <Box key={expense.id} mb={3} p={2} border={1} borderColor="grey.300" borderRadius={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#1976d2' }}>
                  Expense {index + 1}
                </Typography>
                {expenses.length > 1 && (
                  <IconButton
                    color="secondary"
                    onClick={() => removeExpense(expense.id)}
                    size="small"
                    title="Remove this expense"
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
              
              <Grid container spacing={2}>
                {/* Expense Type */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" required error={!!expenseErrors[`${expense.id}_expense_type`]}>
                    <InputLabel>Expense Type</InputLabel>
                    <Select
                      value={expenseDetails[expense.id]?.expense_type || ''}
                      onChange={(e) => handleFieldChange(expense.id, 'expense_type', e.target.value)}
                      label="Expense Type"
                    >
                      {expenseTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                      ))}
                    </Select>
                    {expenseErrors[`${expense.id}_expense_type`] && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {expenseErrors[`${expense.id}_expense_type`]}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Name */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Name"
                    variant="outlined"
                    fullWidth
                    size="small"
                    required
                    value={expenseDetails[expense.id]?.name || ''}
                    onChange={(e) => handleFieldChange(expense.id, 'name', e.target.value)}
                    error={!!expenseErrors[`${expense.id}_name`]}
                    helperText={expenseErrors[`${expense.id}_name`] || ''}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>

                {/* Expense Date */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Expense Date"
                    type="date"
                    fullWidth
                    size="small"
                    required
                    value={expenseDetails[expense.id]?.expense_date || ''}
                    onChange={(e) => handleFieldChange(expense.id, 'expense_date', e.target.value)}
                    error={!!expenseErrors[`${expense.id}_expense_date`]}
                    helperText={expenseErrors[`${expense.id}_expense_date`] || ''}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Amount */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Amount"
                    type="number"
                    fullWidth
                    size="small"
                    required
                    value={expenseDetails[expense.id]?.amount || ''}
                    onChange={(e) => handleFieldChange(expense.id, 'amount', e.target.value)}
                    error={!!expenseErrors[`${expense.id}_amount`]}
                    helperText={expenseErrors[`${expense.id}_amount`] || ''}
                    inputProps={{ step: "0.01" }}
                  />
                </Grid>

                {/* Receiver */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Receiver"
                    variant="outlined"
                    fullWidth
                    size="small"
                    required
                    value={expenseDetails[expense.id]?.reciever || ''}
                    onChange={(e) => handleFieldChange(expense.id, 'reciever', e.target.value)}
                    error={!!expenseErrors[`${expense.id}_reciever`]}
                    helperText={expenseErrors[`${expense.id}_reciever`] || ''}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>

                {/* Payment Method */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" required error={!!expenseErrors[`${expense.id}_mop`]}>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={expenseDetails[expense.id]?.mop || ''}
                      onChange={(e) => handleFieldChange(expense.id, 'mop', e.target.value)}
                      label="Payment Method"
                    >
                      {PAYMENT_METHODS.map((method) => (
                        <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                      ))}
                    </Select>
                    {expenseErrors[`${expense.id}_mop`] && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {expenseErrors[`${expense.id}_mop`]}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Reference */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Reference"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={expenseDetails[expense.id]?.reference || ''}
                    onChange={(e) => handleFieldChange(expense.id, 'reference', e.target.value)}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>

                {/* Expense Receipt */}
                <Grid item xs={12} sm={6} md={3}>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: 'none' }}
                    id={`create-another-receipt-upload-${expense.id}`}
                    onChange={(e) => handleFileChange(expense.id, e.target.files[0])}
                  />
                  <label htmlFor={`create-another-receipt-upload-${expense.id}`}>
                    <Button variant="outlined" component="span" fullWidth size="small">
                      {expenseDetails[expense.id]?.expense_receipt_file?.name || 'Upload Receipt (Optional)'}
                    </Button>
                  </label>
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    variant="outlined"
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={expenseDetails[expense.id]?.notes || ''}
                    onChange={(e) => handleFieldChange(expense.id, 'notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              Creating...
            </>
          ) : (
            'Submit'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateAnotherExpenseModal;

